import { get, post } from './API';

/**
 * The editable guidance a production agent runs on, and its version history.
 *
 * A SCOPE is one agent's guidance — the booking classifier today, other
 * agents later. The list of scopes is owned by the code on the server, not by
 * this screen and not by whoever is typing: `/ai/instructions/scopes` is the
 * only place a scope can come from, so a typo cannot invent one and a scope
 * that no longer exists cannot be written to.
 *
 * Two states per scope, and the whole point of the screen is that they are
 * not the same thing:
 *
 *   published — what the agent is using RIGHT NOW, in production
 *   draft     — what somebody is working on, reaching nobody
 *
 * `published` and `draft` are each
 * `{ version, body, notes, mode, tool_authority, updated_by, updated_at,
 * published_by, published_at }` or null. A scope can have a draft and nothing
 * published, or a published version and no draft, or neither.
 *
 * A VERSION IS THE PROSE, THE MODE AND THE TOOL MAP TOGETHER. `mode` is
 * `shadow` or `live`; `tool_authority` maps a tool's name to `off`, `propose`
 * or `auto`, with absent meaning `off`. They live on the same row as the body
 * and are frozen by the same publish, which is what makes "what was this
 * agent allowed to do when it ran" the same lookup as "what was it told" —
 * every decision already records `instruction_rev`. The screen therefore has
 * ONE draft, ONE save and ONE publish for all three, and the helpers further
 * down are the vocabulary for reading them.
 *
 * SAVING AND PUBLISHING ARE SEPARATE CALLS, deliberately. `saveDraft` writes
 * the draft and changes nothing the agent sees; `publish` is what makes a
 * version live. Nothing in here does both, and nothing should: an edit
 * half-typed must never be live, because this text steers how a production
 * agent classifies real bookings.
 *
 * PUBLISH TAKES A VERSION, not a body — the server publishes something it has
 * already stored, which is why the screen makes you save before you publish.
 *
 * THERE IS NO ROLLBACK CALL. A published version is frozen, and the server
 * REFUSES to publish one again: re-publishing v2 while v4 is live would leave
 * v2 live and newer than the version that superseded it, while every record
 * citing v4 still said v4. Going back to v2 is `getVersion` to read its
 * words, `saveDraft` to store them as the next version, and `publish` on
 * that — forward, under the name of whoever decided to go back. Nothing is
 * ever rewritten or removed.
 *
 * The envelope is `{ success: true, data: <payload> }`, unwrapped here the
 * same way KnowledgeBaseService does it — `?.data || []` for a list, and
 * `?.data || response.data` for a single object so an unwrapped response
 * still reads.
 */

/**
 * The body cap to count against BEFORE a payload has arrived: 8,000.
 *
 * The server owns this number — `AGENT_INSTRUCTION_BODY_MAX_LENGTH` in
 * ph-core — and now serves it as `max_length` on both GET payloads, so
 * `maxLengthFor` below reads the served one and this constant is only the
 * floor for a first render and for a response that somehow carries no cap.
 *
 * IT IS NOT THE RULE. The server's refusal is the authority: this must never
 * truncate (that silently drops the half that mattered) and must never block
 * the save, or the refusal never reaches the screen and a drift between the
 * two numbers becomes invisible. It was 20,000 while the server refused at
 * 8,000 — a counter that is wrong is worse than no counter, because an admin
 * trusts it right up to the save that fails.
 */
export const INSTRUCTIONS_MAX_LENGTH = 8000;

/**
 * The cap to count against: the server's own number whenever a payload
 * carries one, the constant above otherwise. Written this way round so the
 * served number always wins and the screen cannot drift from it.
 */
export function maxLengthFor(payload) {
  const fromServer = Number(payload?.max_length);
  return Number.isFinite(fromServer) && fromServer > 0
    ? fromServer
    : INSTRUCTIONS_MAX_LENGTH;
}

/**
 * WHAT A TOOL AUTHORITY MEANS, in the screen's own words.
 *
 * THE LIST OF AUTHORITIES IS THE SERVER'S — it rides along on the scopes
 * payload as `authorities`, and the radios are built from that, never from
 * the keys below. This is only the wording: three sentences too long to
 * repeat at forty-five rows, and a vocabulary the Activity screen already
 * keeps this way (`DECISION_STATES`). An authority the server adds later
 * still renders, under its own value, through `authorityDescriptor`.
 *
 * `off` IS THE DEFAULT AND IS NEVER STORED. A tool absent from the map is a
 * tool the agent is not handed at all, so the screen reads an absent key as
 * `off` rather than as "unset" — there is no third state.
 */
export const TOOL_AUTHORITIES = [
  { value: 'off', label: 'Off', detail: 'Not in the agent’s list. It cannot be called at all.' },
  {
    value: 'propose',
    label: 'Propose',
    detail: 'The agent may call it. The call does NOT execute — it is captured'
      + ' for a person to approve.',
  },
  { value: 'auto', label: 'Auto', detail: 'The call executes.' },
];

/** The authority an absent key means. Never stored; see above. */
export const DEFAULT_AUTHORITY = 'off';

/**
 * WHETHER THE AGENT ACTS AT ALL — one switch, above every tool.
 *
 * Mirrored rather than served: the scopes payload carries `tools` and
 * `authorities` but NOT the mode list, so this is the one AI-tools vocabulary
 * the screen holds itself. Two members, and the consequence of each is the
 * point of the control, so the words matter more than the list does.
 *
 * `AI_AGENT_MODES` in the API's ph-core/src/Constants/AiTools.ts is the
 * authority. If a third mode is ever added there, the scopes payload should
 * start serving this list too and this constant should go.
 */
export const AGENT_MODES = [
  {
    value: 'shadow',
    label: 'Shadow',
    detail: 'Nothing that changes a record executes, whatever a tool below says. Reads still run.',
  },
  {
    value: 'live',
    label: 'Live',
    detail: 'Each tool’s own authority below decides.',
  },
];

/** A descriptor by value, so a member the server adds later still renders. */
const descriptor = (list, value) => (
  list.find((entry) => entry.value === value)
  || (value ? { value, label: String(value), detail: '' } : null)
);

export const authorityDescriptor = (value) => descriptor(TOOL_AUTHORITIES, value);
export const modeDescriptor = (value) => descriptor(AGENT_MODES, value);

/**
 * The mode a stored version is in.
 *
 * ANYTHING THAT IS NOT THE WORD `live` IS `shadow` — the server's own rule
 * (`effectiveAiToolAuthority`), and it has to be repeated on the read because
 * the absent, the misspelt and the hand-edited all have to land on the state
 * in which nothing happens. A screen that rendered `undefined` as blank would
 * leave an admin unable to tell "not set" from "live".
 */
export const modeOf = (view) => (view?.mode === 'live' ? 'live' : 'shadow');

/**
 * HOW FAR UP THE SCALE AN AUTHORITY SITS — read off the SERVED order.
 *
 * The server states that the order of `AI_TOOL_AUTHORITIES` IS the ranking
 * and every ceiling check is a comparison of two indexes. So the ranking is
 * taken from the array that arrived rather than from a copy here: a member
 * moved deliberately on the server changes what this screen offers without a
 * dashboard change. `-1` for anything not in the list, which therefore ranks
 * below `off` and is never offered.
 */
export const authorityRank = (authorities, value) => (
  (Array.isArray(authorities) ? authorities : []).indexOf(value)
);

/**
 * May a configuration select this authority for this tool?
 *
 * THE CEILING IS THE CODE'S VETO and is not editable from here: `maxAuthority`
 * travels with every tool precisely so the screen can show what the server
 * will refuse instead of offering it and turning a click into a 400. The
 * server still enforces it — this is what the screen SHOWS, not what decides.
 */
export const isAuthorityAllowed = (authorities, tool, value) => (
  authorityRank(authorities, value) >= 0
  && authorityRank(authorities, value) <= authorityRank(authorities, tool?.maxAuthority)
);

/** One tool's configured authority. An absent key is `off`. */
export const authorityOf = (map, key) => (map && map[key]) || DEFAULT_AUTHORITY;

/**
 * WHAT WOULD ACTUALLY HAPPEN, once the scope's mode has had its say.
 *
 * THE ONE PIECE OF SERVER POLICY THIS SCREEN RESTATES, and it restates it
 * because it cannot be honest otherwise: a per-tool map rendered under a mode
 * that overrides it would show forty-five values that are not what the agent
 * would do. In `shadow` every `writes: true` tool comes down to `propose` —
 * captured, not executed — and a read is untouched, which is the whole point
 * of shadowing rather than disabling.
 *
 * `effectiveAiToolAuthority` in the API's ph-core/src/Constants/AiTools.ts is
 * the authority and the enforcement; this only ever LOWERS, exactly as that
 * one does, so the worst a drift here can do is understate what runs.
 */
export const effectiveAuthority = ({ mode, tool, authority }) => (
  mode !== 'live' && tool?.writes && authority === 'auto' ? 'propose' : authority
);

/** The tools a map grants at all — the keys, since `off` is never stored. */
export const grantedTools = (map) => Object.entries(map || {})
  .filter(([, authority]) => authority && authority !== DEFAULT_AUTHORITY)
  .map(([key]) => key);

/**
 * The map as it is stored: granted tools only.
 *
 * `off` IS DROPPED rather than sent, because the server drops it too
 * (`normaliseAiToolAuthority`) and the keys of a stored map are exactly the
 * granted tools. Sending `{ search_bookings: 'off' }` would store nothing and
 * leave the screen holding a key the server does not — one fact, two
 * representations.
 *
 * It always returns an OBJECT, never `undefined`: an omitted `tool_authority`
 * means "leave what is stored" to the server, so an admin who turned the last
 * tool off has to send `{}` for that to be what happens.
 */
export const storableAuthority = (map) => Object.fromEntries(
  Object.entries(map || {}).filter(([, authority]) => (
    authority && authority !== DEFAULT_AUTHORITY
  )),
);

/** Do two authority maps grant the same things? `off` and absent are equal. */
export const sameAuthority = (a, b) => {
  const left = storableAuthority(a);
  const right = storableAuthority(b);
  const keys = [...new Set([...Object.keys(left), ...Object.keys(right)])];
  return keys.every((key) => left[key] === right[key]);
};

/**
 * THE AUTHORITY THAT ACTUALLY EXECUTES. `propose` does not.
 *
 * Not a detail, and not the same question as "is it granted". The autonomous
 * runtime hands the model ONLY the tools on `auto` (`_granted_tools` in
 * `chat-agent/autonomous.py`) and **refuses the whole run** when that set is
 * empty: "No tool in this scope's configuration is set to `auto`, so the agent
 * has nothing it may call." Meanwhile the SAVE-time check that a tool named in
 * the prose is granted passes on `propose` too, because `grantedAiTools` is
 * the keys of the map and `off` is simply absent.
 *
 * So a scope can save cleanly, publish cleanly, and then not run at all. That
 * gap is the single most expensive thing this screen can fail to say out loud.
 */
export const EXECUTING_AUTHORITY = 'auto';

/** The tools that would actually be handed to the model, after the mode. */
export const executableTools = ({ tools, toolAuthority, mode }) => (
  (Array.isArray(tools) ? tools : [])
    .filter((tool) => effectiveAuthority({
      mode, tool, authority: authorityOf(toolAuthority, tool.key),
    }) === EXECUTING_AUTHORITY)
    .map((tool) => tool.key)
);

/**
 * `tool:` FOLLOWED BY A SNAKE_CASE NAME.
 *
 * THE ONE RULE OF THE SERVER'S SCANNER THIS FILE MIRRORS, and the mirror is
 * deliberate and narrow. `aiToolNamesMentionedIn` in the API matches two
 * forms: every catalogue name as a bare word, and this marked form. The bare
 * half needs no copy — the catalogue is SERVED, so the same word-boundary
 * match over the served keys is the same rule by construction, and a tool
 * renamed in Python changes both sides at once.
 *
 * This regex is the half that cannot be derived, because its whole purpose is
 * to catch a name that is NOT in the catalogue — a typo, or a tool since
 * removed. Copied verbatim from `MARKED_NAMES`; the underscore is what keeps
 * ordinary English out of it, so "pick the right tool: check the knowledge
 * base" is not read as a tool called `check`.
 *
 * THE SERVER REMAINS THE AUTHORITY. Everything below is advisory — its job is
 * to stop an author ever MEETING the refusal, not to decide anything. If the
 * two ever need to diverge, the right fix is an endpoint that lints a body,
 * not a cleverer copy here.
 */
const MARKED_NAMES = /\btool:\s*`?([a-z][a-z0-9]*(?:_[a-z0-9]+)+)`?/gi;

/** Escape a tool key for use inside a RegExp. Keys are snake_case today. */
const escapeKey = (key) => String(key).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Every tool name a body refers to, as written, de-duplicated.
 *
 * The bare half is built from the SERVED catalogue, so there is no list of
 * tool names in this repository — which is the whole point of the catalogue
 * riding along on the scopes payload.
 */
export const toolNamesMentionedIn = (body, tools) => {
  const text = typeof body === 'string' ? body : '';
  const keys = (Array.isArray(tools) ? tools : []).map((t) => t?.key).filter(Boolean);
  const names = [];

  if (keys.length > 0) {
    const bare = new RegExp(`\\b(${keys.map(escapeKey).join('|')})\\b`, 'g');
    names.push(...[...text.matchAll(bare)].map((m) => m[1]));
  }

  names.push(...[...text.matchAll(MARKED_NAMES)].map((m) => m[1]));

  return [...new Set(names)];
};

/**
 * WHERE THE PROSE AND THE TOOL MAP DISAGREE — before the server says so.
 *
 * The owner's intended workflow is "when this happens, call this tool for
 * results, then create a price". Written that way against a scope with nothing
 * granted, the save is refused once per name — sixteen opaque errors for a
 * body that says exactly what it was asked to say. The rule is right; meeting
 * it as a server refusal is the bug.
 *
 * Four answers, because they want four different things done about them:
 *
 *   `ungranted`   named in the prose, not in the map. The save WILL be
 *                 refused. One click grants it.
 *   `unknown`     named via `tool:` and not a tool at all — a typo or a
 *                 removed tool. Granting is impossible; the name has to go.
 *   `proposeOnly` named in the prose AND granted, but not at an authority
 *                 that executes. Saves fine, publishes fine, then never runs.
 *   `unmentioned` granted but never named in the prose. Not a fault at all —
 *                 a tool's own description is what the model reads, so good
 *                 guidance rarely names one. Listed because "did I forget to
 *                 write the rule for this?" is a fair question.
 */
export const reconcileGuidanceTools = ({
  body, tools, toolAuthority, mode,
}) => {
  const catalogue = Array.isArray(tools) ? tools : [];
  const mentioned = toolNamesMentionedIn(body, catalogue);
  const granted = new Set(grantedTools(toolAuthority));
  const executing = new Set(executableTools({ tools: catalogue, toolAuthority, mode }));
  const isTool = (name) => catalogue.some((tool) => tool.key === name);

  return {
    ungranted: mentioned.filter((name) => isTool(name) && !granted.has(name)),
    unknown: mentioned.filter((name) => !isTool(name)),
    proposeOnly: mentioned.filter((name) => (
      isTool(name) && granted.has(name) && !executing.has(name)
    )),
    unmentioned: [...granted].filter((name) => !mentioned.includes(name)),
  };
};

/**
 * THE CONFIGURATION AN EDITOR SHOULD OPEN ON — the draft's, or what is live.
 *
 * IT MIRRORS WHAT THE SERVER WOULD INHERIT, and it has to. `configFor` reads
 * an absent `mode`/`tool_authority` as "keep what the latest version has", so
 * a screen that opened on `shadow` and an empty map while a published version
 * granted twelve tools would, on the very next save, SEND that empty map and
 * revoke all twelve — an admin who came to fix a typo in the prose would take
 * the agent's tools away without touching the tool map.
 *
 * So the basis is the draft when there is one, the published version
 * otherwise, and the safe defaults when there is neither: `shadow`, and
 * nothing granted. The map is copied rather than aliased, because the editor
 * mutates it and the payload is also what `dirty` is measured against.
 */
export const draftConfigFrom = (payload) => {
  const basis = payload?.draft || payload?.published || null;
  return {
    mode: modeOf(basis),
    toolAuthority: { ...(basis?.tool_authority || {}) },
  };
};

/**
 * The field-level messages behind a refused call, which is where this API
 * puts the reason — `message` is the constant "The validation has failed".
 */
export const apiFieldMessages = (err) => {
  const errors = err?.response?.data?.errors;
  return (Array.isArray(errors) ? errors : [])
    .map((e) => (typeof e?.message === 'string' ? e.message : ''))
    .filter(Boolean);
};

/**
 * Is this error the endpoint simply not being deployed here yet?
 *
 * Worth telling apart from a refusal, and only until the endpoint lands: a
 * screen that answered "the request failed" while the truth was "this half is
 * still being built" would send an admin looking for a fault in an
 * instruction that is fine.
 *
 * ── IT IS A 400 TODAY, NOT A 404, AND THAT HAD TO BE CHECKED ──
 * `/ai/instructions/…` is served by a `{proxy+}` child, so API Gateway hands
 * the existing Lambda EVERY path under it — including one it has no route
 * for. That Lambda's `routeOf` refuses an unrecognised last segment with a
 * ValidationError rather than falling through to the bare route (deliberately:
 * answering `/ai/instructions/nonsense` with the scope's whole state is how a
 * client comes to depend on a URL that does not exist). A ValidationError is
 * the house 400, so the real answer to this call right now is
 *
 *   400 { message: 'The validation has failed',
 *         errors: [{ field: 'path', message: 'Unknown route /ai/instructions/<route>' }] }
 *
 * Matching on 404 alone would therefore have missed it and shown "The
 * validation has failed" — the exact generic error this is here to avoid.
 *
 * The 400 branch reads the server's own words, which is brittle by nature, so
 * it is deliberately narrow: only a `path` problem that says "unknown route".
 * Anything else is a real refusal and is shown as one. 404 and 501 stay, for
 * whatever shape the endpoint's own deployment gap turns out to have.
 */
export const isNotDeployed = (err) => {
  const status = err?.response?.status;

  if (status === 404 || status === 501) return true;
  if (status !== 400) return false;

  return apiFieldMessages(err).some((message) => /unknown route/i.test(message));
};

export default {
  /**
   * EVERYTHING THE BUILDER NEEDS BEFORE IT HAS PICKED A SCOPE, in one read:
   * `{ scopes, tools, authorities }`.
   *
   * The server answers `{ data: { scopes, max_length, tools, authorities } }`.
   *
   * `max_length` IS DELIBERATELY NOT RETURNED. The one on this payload is the
   * DEFAULT cap, and it is not the cap that will be enforced: each scope
   * carries its own (`bodyMaxLength`, and one of the two scopes is larger),
   * and the per-scope read serves the real one as `max_length` — which is
   * what `maxLengthFor` reads. Handing a second, smaller number to the editor
   * would be a counter that is wrong, and a counter that is wrong is worse
   * than no counter because an admin trusts it right up to the save.
   * `tools` is the whole tool catalogue — `{ key, label, module, writes,
   * maxAuthority }` for each of the forty-five — and `authorities` is the
   * three-member ranking. THEY ARE SERVED SO THAT THE DASHBOARD CANNOT HOLD A
   * SECOND COPY: a tool's name is the name of a Python `@tool` function, and
   * the one list that mirrors those lives on the server beside the gate that
   * checks it.
   *
   * Returned already unpacked, with every key defaulted — the shape
   * `AiActivityService.list` uses, and for the same reason: a reset jest mock
   * answering `undefined` has to render an empty screen rather than throw.
   *
   * THE PAYLOAD IS ONE LEVEL DEEPER THAN THE OTHER READS on this service,
   * which return the whole `data` object. Worth stating because getting it
   * wrong is silent and has happened: an earlier version returned `data`
   * itself, the screen's `Array.isArray(list) ? list : []` took the empty
   * branch, and the only agent in the system rendered as "No agents
   * available" while the endpoint was answering 200 with it.
   *
   * This replaced `listScopes`, which returned the scopes array alone and
   * threw the catalogue away.
   */
  getCatalogue: async () => {
    const response = await get('/ai/instructions/scopes');
    const body = response?.data?.data || {};
    return {
      scopes: body.scopes || [],
      tools: body.tools || [],
      authorities: body.authorities || [],
    };
  },

  /** One scope's published version, draft and history, in one read. */
  getForScope: async (scope) => {
    const response = await get('/ai/instructions', { scope });
    return response.data?.data || response.data;
  },

  /**
   * ONE stored version in full, by number — the read a rollback is built on.
   *
   * The history rows carry no bodies, deliberately: a body is capped at 8 KB
   * and a scope with forty versions would ship a third of a megabyte on every
   * page load to show a table of dates. So a body is fetched one at a time,
   * when somebody asks to read one.
   *
   * This is NOT a restore. The server refuses to publish an already-published
   * version; going back to v3 is v3's words saved as a new draft and published
   * forward. See the publish call below.
   */
  getVersion: async ({ scope, version }) => {
    const response = await get('/ai/instructions', { scope, version });
    return response.data?.data || response.data;
  },

  /**
   * WHAT THE MODEL ACTUALLY RECEIVES, composed, read-only.
   *
   * ════════════════════════════════════════════════════════════════════════
   * THIS ENDPOINT DOES NOT EXIST YET. THE DASHBOARD MUST NOT HOLD THE PROMPT.
   * ════════════════════════════════════════════════════════════════════════
   *
   *   GET /ai/instructions/prompt?scope=…[&version=N]
   *   → { scope, version | null, code_owned, guidance, composed }
   *
   * `code_owned` the code's own prompt for this scope, verbatim — the part no
   *              published guidance can relax. A deploy changes it.
   * `guidance`   the admin body that was fenced in, or `null` when nothing is
   *              published. `null` is a real answer, not an empty string.
   * `composed`   exactly what the model is handed:
   *              `composeSystemPrompt(code_owned, instruction)`.
   * `version`    which instruction version `guidance` came from, or `null`.
   *
   * WITHOUT `version` it answers for what is LIVE. With one it answers for
   * that stored version, so an admin can see what a draft WOULD send before
   * publishing it — the same read-a-version shape `getVersion` already has.
   *
   * ── WHY THE SERVER HAS TO SERVE IT, RATHER THAN THE SCREEN BUILDING IT ──
   *
   * Three reasons, and none of them is convenience:
   *
   *   1. THE CODE-OWNED PROMPT IS NOT THE DASHBOARD'S TO HOLD. Retyping it
   *      here would be a second copy of a production prompt — the exact
   *      duplication this codebase forbids — and it would go stale silently,
   *      which for a prompt means the screen confidently showing an admin
   *      rules the model is not actually running under.
   *   2. THERE IS NO SCOPE → PROMPT MAPPING ANYWHERE YET. Each consumer holds
   *      its own and passes it in: `AutonomousDraftService.CODE_OWNED_PROMPT`
   *      for `customer-service`, `InboundTriageService.INBOUND_TRIAGE_PROMPT`
   *      for `customer-service-triage`, and `AgentReviewService` takes the
   *      caller's for `sync-review`. Only the server can resolve a scope to
   *      its prompt, and building that registry is an API change.
   *   3. THE COMPOSITION IS ITS OWN RULE. `composeSystemPrompt` puts the code
   *      prompt first, then a preamble that tells the model the block is
   *      reference material with no authority, then the body inside
   *      `<operator_guidance scope=… version=…>`. Re-implementing that here
   *      would be a second opinion about what the model sees.
   *
   * Returned already unpacked with every key defaulted, so a reset mock or a
   * server that answers nothing renders the honest empty state rather than
   * throwing. `composed` falling back to `code_owned` matches the server's own
   * behaviour for an absent or empty body.
   */
  getComposedPrompt: async ({ scope, version } = {}) => {
    const params = { scope };
    if (version !== undefined && version !== null) params.version = version;
    const response = await get('/ai/instructions/prompt', params);
    const body = response?.data?.data || response?.data || {};
    return {
      scope: body.scope || scope,
      version: body.version === undefined ? null : body.version,
      code_owned: body.code_owned || '',
      guidance: body.guidance === undefined ? null : body.guidance,
      composed: body.composed || body.code_owned || '',
    };
  },

  /**
   * Save (or replace) the DRAFT for a scope. Publishes nothing.
   *
   * THE PROSE, THE MODE AND THE TOOL MAP ARE ONE DRAFT, because they are one
   * ROW on the server: `agent_instructions` carries `body`, `mode` and
   * `tool_authority` together, they are validated against each other on the
   * way in, and publishing freezes all three under one version. That is why
   * there is one save on this screen and not three — and why "what was this
   * agent allowed to do when it ran" is the same lookup as "what was it told".
   *
   * `mode` AND `tool_authority` ARE OMITTED WHEN NOT GIVEN, not defaulted
   * here. The server reads an absent one as "leave what is stored"
   * (`configFor`), which is the right answer for a caller that is only
   * editing prose; a default invented here would silently rewrite a
   * configuration the caller never looked at. The builder always sends both.
   *
   * Empty notes are dropped rather than sent as `notes: ''` — the field is
   * optional and an empty string is a value, not an absence.
   */
  saveDraft: async ({
    scope, body, notes, mode, toolAuthority,
  }) => {
    const payload = { scope, body };
    if (notes) payload.notes = notes;
    if (typeof mode === 'string' && mode) payload.mode = mode;
    // An empty map is a real instruction — "grant nothing" — so the test is
    // for an object, not for a truthy one.
    if (toolAuthority && typeof toolAuthority === 'object') {
      payload.tool_authority = storableAuthority(toolAuthority);
    }
    const response = await post('/ai/instructions', payload);
    return response.data?.data || response.data;
  },

  /**
   * Make a stored DRAFT live for the agent. The only call that does.
   *
   * A version that is already published is refused — that refusal is how "no
   * revert" is enforced, and its message says to save the text as a new draft
   * instead. So the only version this is ever called with is the draft's.
   */
  publish: async ({ scope, version }) => {
    const response = await post('/ai/instructions/publish', { scope, version });
    return response.data?.data || response.data;
  },
};
